/**
 * Cookie 工具函数
 */

/**
 * 获取 Cookie Domain
 * 根据环境变量决定 cookie 的 domain
 * @returns cookie domain 字符串
 */
export const getCookieDomain = (): string => {
  const currentDomain = window.location.hostname;

  // 海外线上环境
  if (currentDomain.includes('codeflicker.ai')) {
    return 'codeflicker.ai';
  }

  // codeflicker-toc pre环境，cookie 种到 gifshow.com
  if (currentDomain.includes('test.gifshow.com')) {
    return '.gifshow.com';
  }

  // staging 环境或 localhost
  if (currentDomain.includes('staging.kuaishou.com') || currentDomain === 'localhost') {
    return '.staging.kuaishou.com';
  }

  // 其他环境（包括海外预发）使用当前域名
  const currentHost = window.location.hostname;
  return currentHost;
};

/**
 * 获取当前域名的二级域名
 * 例如：www.example.com -> .example.com
 *       sub.example.com -> .example.com
 *       localhost -> localhost
 */
export const getSecondLevelDomain = (): string => {
  const hostname = window.location.hostname;

  // 如果是 localhost 或 IP 地址，直接返回
  if (hostname === 'localhost' || /^\d+\.\d+\.\d+\.\d+$/.test(hostname)) {
    return hostname;
  }

  // 分割域名
  const parts = hostname.split('.');

  // 如果只有两部分（如 example.com），返回 .example.com
  if (parts.length === 2) {
    return `.${hostname}`;
  }

  // 如果有多部分（如 www.example.com），返回 .example.com
  if (parts.length > 2) {
    return `.${parts.slice(-2).join('.')}`;
  }

  return hostname;
};

/**
 * 设置 Cookie
 * @param name Cookie 名称
 * @param value Cookie 值
 * @param options Cookie 选项
 */
export interface CookieOptions {
  expires?: Date | number; // 过期时间（Date 对象或天数）
  path?: string; // 路径，默认 '/'
  domain?: string; // 域名
  secure?: boolean; // 是否只在 HTTPS 下传输
  sameSite?: 'Strict' | 'Lax' | 'None'; // SameSite 属性
}

export const setCookie = (name: string, value: string, options: CookieOptions = {}): void => {
  const {
    expires,
    path = '/',
    domain = getSecondLevelDomain(),
    secure = false,
    sameSite = 'Lax',
  } = options;

  let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;

  if (expires) {
    let expiresDate: Date;
    if (typeof expires === 'number') {
      expiresDate = new Date();
      expiresDate.setTime(expiresDate.getTime() + expires * 24 * 60 * 60 * 1000);
    } else {
      expiresDate = expires;
    }
    cookieString += `; expires=${expiresDate.toUTCString()}`;
  }

  if (path) {
    cookieString += `; path=${path}`;
  }

  if (domain) {
    cookieString += `; domain=${domain}`;
  }

  if (secure) {
    cookieString += '; secure';
  }

  if (sameSite) {
    cookieString += `; samesite=${sameSite}`;
  }

  document.cookie = cookieString;
};

/**
 * 获取 Cookie
 * @param name Cookie 名称
 * @returns Cookie 值，如果不存在返回 null
 */
export const getCookie = (name: string): string | null => {
  const nameEQ = `${encodeURIComponent(name)}=`;
  const cookies = document.cookie.split(';');

  for (let i = 0; i < cookies.length; i++) {
    let cookie = cookies[i];
    while (cookie.charAt(0) === ' ') {
      cookie = cookie.substring(1, cookie.length);
    }
    if (cookie.indexOf(nameEQ) === 0) {
      return decodeURIComponent(cookie.substring(nameEQ.length, cookie.length));
    }
  }

  return null;
};

/**
 * 删除 Cookie
 * @param name Cookie 名称
 * @param options Cookie 选项（需要匹配设置时的 path 和 domain）
 */
export const deleteCookie = (name: string, options: CookieOptions = {}): void => {
  setCookie(name, '', {
    ...options,
    expires: new Date(0),
  });
};
